import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ParkingGateListResponse } from '../../../../../../core/models/parking.model';
import { ActionBackOfficeResponse } from '../../../../../../core/models/api.model';
import { ForceEnterTransactionPayload } from '../../../../../../core/models/parking/transaction-parking.model';
import { ParkingVehicleTypeResponse } from '../../../../../../core/models/vehicle.model';
import { ApiService } from '../../../../../../core/services/api.service';
import { ApiFormService } from '../../../../../../core/services/api-form.service';
import { ApiTransactionParkingService } from '../../../../../../core/services/api/parking/api-transaction-parking.service';
import { MenuPermissionService } from '../../../../../../core/services/menu-permission.service';
import { PssConfirmModalComponent } from '../../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../../../tools/template/pss/pss-loading/pss-loading.component';

export const FORCE_ENTER_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  },
};

@Component({
  selector: 'app-modal-force-enter-transaction',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PssConfirmModalComponent,
    PssLoadingComponent,
  ],
  templateUrl: './modal-force-enter-transaction.component.html',
  styleUrl: './modal-force-enter-transaction.component.css',
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: FORCE_ENTER_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
})
export class ModalForceEnterTransactionComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() siteId = 0;
  @Input() userId = 0;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSuccess = new EventEmitter<void>();

  private readonly apiTx = inject(ApiTransactionParkingService);
  private readonly apiService = inject(ApiService);
  private readonly apiForm = inject(ApiFormService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  licensePlate = '';
  parkingGateId = 0;
  vehicleTypeId = 0;
  comeInDate: Date | null = null;
  comeInTime = '';
  reason = '';
  openGate = false;
  isSubmitting = false;
  showConfirm = false;
  confirmMessage = '';

  /** optional รูปทางเข้า */
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  imageFileName = '';

  entryGates: ParkingGateListResponse[] = [];
  vehicleTypes: ParkingVehicleTypeResponse[] = [];

  gateSearchText = '';
  isGateDropdownOpen = false;
  vehicleTypeSearchText = '';
  isVehicleTypeDropdownOpen = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetForm();
      this.loadMasters();
    }
  }

  get filteredGates(): ParkingGateListResponse[] {
    const q = this.gateSearchText.trim().toLowerCase();
    if (!q) return this.entryGates;
    return this.entryGates.filter((g) => g.name.toLowerCase().includes(q));
  }

  get filteredVehicleTypes(): ParkingVehicleTypeResponse[] {
    const q = this.vehicleTypeSearchText.trim().toLowerCase();
    if (!q) return this.vehicleTypes;
    return this.vehicleTypes.filter((v) => v.typeName.toLowerCase().includes(q));
  }

  get selectedGateName(): string {
    return this.entryGates.find((g) => g.id === this.parkingGateId)?.name ?? '';
  }

  get selectedVehicleTypeName(): string {
    return this.vehicleTypes.find((v) => v.id === this.vehicleTypeId)?.typeName ?? '';
  }

  private resetForm(): void {
    this.licensePlate = '';
    this.parkingGateId = 0;
    this.vehicleTypeId = 0;
    const now = new Date();
    this.comeInDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.comeInTime = this.toTimeInput(now);
    this.reason = '';
    this.openGate = false;
    this.isSubmitting = false;
    this.showConfirm = false;
    this.confirmMessage = '';
    this.clearImage();
    this.gateSearchText = '';
    this.vehicleTypeSearchText = '';
    this.isGateDropdownOpen = false;
    this.isVehicleTypeDropdownOpen = false;
  }

  private loadMasters(): void {
    if (this.siteId <= 0) return;

    this.apiService.getParkingGate(this.siteId).subscribe({
      next: (res) => {
        this.entryGates = (res ?? []).filter((g) => this.isEntryGate(g));
      },
      error: () => (this.entryGates = []),
    });

    this.apiForm.getParkingVehicleType().subscribe({
      next: (res) => (this.vehicleTypes = res ?? []),
      error: () => (this.vehicleTypes = []),
    });
  }

  selectGate(gate: ParkingGateListResponse): void {
    this.parkingGateId = gate.id;
    this.gateSearchText = gate.name;
    this.isGateDropdownOpen = false;
  }

  selectVehicleType(vt: ParkingVehicleTypeResponse): void {
    this.vehicleTypeId = vt.id;
    this.vehicleTypeSearchText = vt.typeName;
    this.isVehicleTypeDropdownOpen = false;
  }

  closeDropdowns(): void {
    this.isGateDropdownOpen = false;
    this.isVehicleTypeDropdownOpen = false;
    if (this.parkingGateId > 0) {
      this.gateSearchText = this.selectedGateName;
    }
    if (this.vehicleTypeId > 0) {
      this.vehicleTypeSearchText = this.selectedVehicleTypeName;
    }
  }

  close(): void {
    if (this.isSubmitting) return;
    this.showConfirm = false;
    this.onClose.emit();
  }

  askSubmit(): void {
    if (this.isSubmitting) return;

    if (!this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้างรายการเข้า');
      return;
    }

    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId) {
      this.toast.error('ไม่พบ Menu ID');
      return;
    }
    if (this.siteId <= 0 || this.userId <= 0) {
      this.toast.error('กรุณาเลือกลานและเข้าสู่ระบบให้ถูกต้อง');
      return;
    }

    const licensePlate = this.licensePlate.replace(/\s+/g, '');
    if (!licensePlate) {
      this.toast.error('กรุณาระบุทะเบียน');
      return;
    }
    if (this.parkingGateId <= 0) {
      this.toast.error('กรุณาเลือกประตูเข้า');
      return;
    }
    if (this.vehicleTypeId <= 0) {
      this.toast.error('กรุณาเลือกประเภทรถ');
      return;
    }
    if (!this.comeInDate) {
      this.toast.error('กรุณาเลือกวันเข้า');
      return;
    }
    const comeInCombined = this.combineDateTime(this.comeInDate, this.comeInTime);
    if (!comeInCombined) {
      this.toast.error('กรุณาระบุเวลาเข้า (HH:mm)');
      return;
    }
    const reason = this.reason.trim();
    if (!reason) {
      this.toast.error('กรุณาระบุเหตุผล');
      return;
    }

    const dateLabel = this.formatDateDisplay(this.comeInDate);
    const timeLabel = this.normalizeTime24(this.comeInTime) ?? this.comeInTime;
    this.confirmMessage =
      `ทะเบียน ${licensePlate} | ประตู ${this.selectedGateName || '-'} | ` +
      `${dateLabel} ${timeLabel}` +
      (this.openGate ? ' | เปิดไม้กั้น' : '') +
      (this.imageBase64 ? ' | มีแนบรูป' : '');
    this.showConfirm = true;
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      input.value = '';
      return;
    }
    // จำกัด ~5MB ก่อน encode
    if (file.size > 5 * 1024 * 1024) {
      this.toast.error('ขนาดรูปต้องไม่เกิน 5MB');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      this.imagePreviewUrl = result;
      // ส่งทั้ง data URL ได้ — API จะ normalize เอง
      this.imageBase64 = result;
      this.imageFileName = file.name;
    };
    reader.onerror = () => {
      this.toast.error('อ่านไฟล์รูปไม่สำเร็จ');
      this.clearImage();
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  clearImage(): void {
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.imageFileName = '';
  }

  closeConfirm(): void {
    if (this.isSubmitting) return;
    this.showConfirm = false;
  }

  confirmSubmit(): void {
    if (this.isSubmitting) return;
    this.showConfirm = false;
    this.submit();
  }

  private submit(): void {
    if (this.isSubmitting) return;

    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId || !this.comeInDate) return;

    const licensePlate = this.licensePlate.replace(/\s+/g, '');
    const comeInCombined = this.combineDateTime(this.comeInDate, this.comeInTime);
    if (!comeInCombined) return;

    const payload: ForceEnterTransactionPayload = {
      userId: this.userId,
      menuId,
      siteId: this.siteId,
      openGate: this.openGate,
      parkingGateId: this.parkingGateId,
      comeInTime: this.toYmdHis(comeInCombined),
      licensePlate,
      vehicleTypeId: this.vehicleTypeId,
      reason: this.reason.trim(),
      imageBase64: this.imageBase64 || null,
    };

    this.isSubmitting = true;
    this.apiTx.forceEnterTransaction(payload).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        console.log('forceEnterTransaction', res);
        const raw = res as ActionBackOfficeResponse & Record<string, unknown>;
        const ok = Boolean(res?.isSuccess ?? raw['IsSuccess']);
        const message = String(res?.message ?? raw['Message'] ?? '');
        if (ok) {
          this.toast.success(message || 'ทำรายการเข้าสำเร็จ');
          this.onSuccess.emit();
          this.close();
        } else {
          this.toast.error(message || 'ทำรายการเข้าไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toast.error(err?.error?.message || err?.error || 'ทำรายการเข้าไม่สำเร็จ');
      },
    });
  }

  private formatDateDisplay(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  /** รับเฉพาะตัวเลข + คั่นด้วย : รูปแบบ 24 ชม. เช่น 14:30 */
  onComeInTimeInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    let raw = (el.value || '').replace(/[^\d]/g, '').slice(0, 4);
    if (raw.length >= 3) {
      raw = `${raw.slice(0, 2)}:${raw.slice(2)}`;
    }
    this.comeInTime = raw;
    el.value = raw;
  }

  normalizeComeInTime(): void {
    const normalized = this.normalizeTime24(this.comeInTime);
    if (normalized) this.comeInTime = normalized;
  }

  private combineDateTime(date: Date, time: string): Date | null {
    const normalized = this.normalizeTime24(time);
    if (!normalized) return null;
    const [hhStr, mmStr] = normalized.split(':');
    const hh = Number(hhStr);
    const mm = Number(mmStr);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm, 0, 0);
  }

  private normalizeTime24(time: string): string | null {
    const m = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
    if (!m) return null;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }

  private toTimeInput(d: Date): string {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /** ประตูเข้า — parking_gate_type.entry ต้องเป็น true */
  private isEntryGate(g: ParkingGateListResponse): boolean {
    const raw = g as ParkingGateListResponse & { Entry?: boolean };
    return g.entry === true || raw.Entry === true;
  }

  /** Date → YmdHis */
  private toYmdHis(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  }
}
